import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Group } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput, PasswordInput, Checkbox } from '@/components/ui/inputs';
import { IconMail, IconLock, IconSparkles } from '@tabler/icons-react';
import { FadeIn, AnimatedFormWrapper } from '@/components/animations';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { validators } from '@/utils/validation';
import { toast } from '@/components/ui/providers/AlertProvider';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useEnrichedAuth();

  const validate = (): boolean => {
    let isValid = true;

    // Validate email
    const emailValidation = validators.email(email);
    if (emailValidation !== true) {
      setEmailError(emailValidation);
      toast.error('Validation Error', emailValidation);
      isValid = false;
    } else {
      setEmailError('');
    }

    // Validate password
    const passwordValidation = validators.required(password);
    if (passwordValidation !== true) {
      setPasswordError(passwordValidation);
      toast.error('Validation Error', passwordValidation);
      isValid = false;
    } else {
      setPasswordError('');
    }

    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await toast.promise(
        login(email, password, rememberMe),
        {
          loading: 'Signing in...',
          success: 'Successfully signed in!',
          error: (err: any) => {
            const errorMessage = err?.response?.data?.message || err?.message || 'Invalid email or password';
            return errorMessage;
          },
        }
      );
      // Navigation will be handled by App.tsx routing based on onboardingCompleted status
    } catch (err) {
      console.error('Login failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <FadeIn delay={300}>
        <Stack align="center" gap="xs">
          <Badge
            leftSection={<IconSparkles size={12} />}
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            size="lg"
          >
            Welcome Back
          </Badge>
          <Title order={2} ta="center" mt="md">
            Sign in to your account
          </Title>
          <Text c="dimmed" size="sm" ta="center">
            Enter your credentials to continue
          </Text>
        </Stack>
      </FadeIn>

      {/* Form with staggered animations */}
      <form onSubmit={handleSubmit}>
        <AnimatedFormWrapper animation="stagger" delay={400} staggerDelay={100}>
          <Stack gap="xs">
            <Text size="sm" fw={500} style={{ textAlign: 'left' }}>
              Email
            </Text>
            <TextInput
              placeholder="your@email.com"
              leftSection={<IconMail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
              required
              size="md"
              radius="md"
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500} style={{ textAlign: 'left' }}>
              Password
            </Text>
            <PasswordInput
              placeholder="Your password"
              leftSection={<IconLock size={16} />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              required
              size="md"
              radius="md"
            />
          </Stack>

          <Group justify="space-between">
            <Checkbox
              label="Remember me"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.currentTarget.checked)}
            />
            <Anchor
              component={Link}
              to="/auth/forgot-password"
              size="sm"
            >
              Forgot password?
            </Anchor>
          </Group>

          <Button
            type="submit"
            fullWidth
            size="md"
            radius="md"
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            isBusy={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </Button>
        </AnimatedFormWrapper>
      </form>

      {/* Register Link */}
      <FadeIn delay={600}>
        <Stack gap="sm">
          <Text ta="center" size="sm">
            Don't have an account?{' '}
            <Anchor component={Link} to="/auth/vendor-register" fw={500}>
              Register as a vendor
            </Anchor>
          </Text>
          <Button
            component={Link}
            to="/auth/vendor-register"
            variant="light"
            fullWidth
            size="md"
            radius="md"
          >
            Create Vendor Account
          </Button>
        </Stack>
      </FadeIn>
    </Stack>
  );
}
